import { useCallback, useEffect, useState } from "react";
import { Loader2, UserX } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

export const dormancy = (u) => {
  if (u.days_since_login !== undefined) return u.days_since_login;
  if (!u.last_login_at) return null;
  return Math.floor((Date.now() - new Date(u.last_login_at).getTime()) / 86400000);
};

export const sinceLabel = (days) => {
  if (days === null || days === undefined) return "never signed in";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

export const AccessReview = ({ canWrite, onChanged }) => {
  const [days, setDays] = useState("90");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/access-review?days=${days}`);
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const disable = async (person) => {
    setBusy(person.id);
    try {
      await api.patch(`/admin/users/${person.id}`, { status: "disabled" });
      toast.success(`${person.name} can no longer sign in`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return null;
  const dormant = data.items.filter((i) => i.dormant);

  return (
    <Panel testId="panel-access-review" kicker="Access review"
           title={`Nobody has signed into these for ${data.threshold_days} days`}
           actions={(
             <Select value={days} onValueChange={setDays}>
               <SelectTrigger className="h-9 w-[150px]" data-testid="access-review-days">
                 <SelectValue /></SelectTrigger>
               <SelectContent>
                 {data.choices.map((c) => (
                   <SelectItem key={c} value={String(c)}>{c} days</SelectItem>
                 ))}
               </SelectContent>
             </Select>
           )}>
      <div className="grid grid-cols-2 gap-3 px-4 pt-3 lg:grid-cols-4">
        <Kpi testId="access-dormant" label="Dormant accounts" accent="amber"
             value={data.counts.dormant} sub={`of ${data.counts.people} people`} />
        <Kpi testId="access-never" label="Never signed in" value={data.counts.never_signed_in}
             sub="provisioned but never used" />
        <Kpi testId="access-recent" label="Signed in recently" accent="emerald"
             value={data.counts.signed_in_recently}
             sub={`within ${data.threshold_days} days`} />
        <Kpi testId="access-disabled" label="Already disabled" value={data.counts.disabled}
             sub="cannot sign in" />
      </div>

      {dormant.length === 0 ? (
        <p className="px-4 py-4 text-[12.5px] text-muted-foreground" data-testid="access-review-clear">
          Every active account has been used inside {data.threshold_days} days. Nothing to close off.
        </p>
      ) : (
        <Table testId="access-review-table"
               head={<><Th>Who</Th><Th>Primary role</Th><Th>Last signed in</Th><Th>Dormant for</Th><Th /></>}>
          {dormant.map((person) => (
            <tr key={person.id} className="row-hover"
                data-testid={`dormant-${person.email}`}>
              <Td>
                <div className="text-[12.5px] font-medium text-foreground">{person.name}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{person.email}</div>
                {person.on_temporary_password && (
                  <Chip className="mt-1 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                    never used its temporary password
                  </Chip>
                )}
              </Td>
              <Td className="text-[11.5px] text-muted-foreground">{person.role}</Td>
              <Td className="font-mono text-[11px]">
                {person.last_login_at ? dateTime(person.last_login_at) : "never"}
              </Td>
              <Td data-testid={`dormant-days-${person.email}`}>
                <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                  {person.days_since_login === null
                    ? `provisioned ${person.days_since_created} days ago`
                    : `${person.days_since_login} days`}
                </Chip>
              </Td>
              <Td>
                {canWrite && (
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" disabled={busy === person.id}
                            data-testid={`disable-dormant-${person.email}`}
                            onClick={() => disable(person)}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">
                      {busy === person.id ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <UserX className="mr-1 h-3 w-3" />} Close it off
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
};
